import { Suspense, use, useMemo } from "react";
import { SchemaV4PageRenderer } from "@cogo/react-renderer/adapters/schema-v4-page";
import { loadPageTree } from "../lib/capture-loader";
import { ProvenanceFooter } from "../components/ProvenanceFooter";
import type { CartItem } from "../App";

interface CheckoutProps {
  cart: CartItem[];
  onNavigate: (path: string) => void;
  onClearCart: () => void;
  isCheckoutSuccess: boolean;
  setIsCheckoutSuccess: (success: boolean) => void;
}

function CheckoutInner({
  cart,
  onNavigate,
  onClearCart,
  isCheckoutSuccess,
  setIsCheckoutSuccess,
}: CheckoutProps) {
  const page = use(loadPageTree("checkout"));

  const subtotal = useMemo(() => {
    return cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
  }, [cart]);

  const shipping = useMemo(() => {
    return subtotal > 0 ? 9.99 : 0;
  }, [subtotal]);

  const total = useMemo(() => {
    return subtotal + shipping;
  }, [subtotal, shipping]);

  // Deep clone and dynamically update the Checkout Cogo JSON tree in memory.
  const pageWithState = useMemo(() => {
    const cloned = JSON.parse(JSON.stringify(page));

    // Construct the dynamic item rows
    const itemRows = cart.map((item) => ({
      id: `cart-item-row-${item.id}`,
      type: "container",
      layout: {
        type: "flex",
        direction: "row",
        align: "center",
        justify: "between",
      },
      className: "py-3 border-b border-white/5 gap-4",
      children: [
        {
          id: `cart-item-left-${item.id}`,
          type: "container",
          layout: {
            type: "flex",
            direction: "row",
            align: "center",
            gap: "12",
          },
          children: [
            {
              id: `cart-item-img-${item.id}`,
              type: "image",
              src: item.image,
              alt: item.name,
              className: "w-10 h-10 object-cover rounded-lg border border-white/10 shadow-inner",
            },
            {
              id: `cart-item-details-${item.id}`,
              type: "container",
              layout: {
                type: "flex",
                direction: "column",
              },
              children: [
                {
                  id: `cart-item-name-${item.id}`,
                  type: "text",
                  text: item.name,
                  className: "text-sm font-semibold text-white",
                },
                {
                  id: `cart-item-qty-${item.id}`,
                  type: "text",
                  text: `Quantity: ${item.quantity}`,
                  className: "text-xs text-slate-400 mt-0.5",
                },
              ],
            },
          ],
        },
        {
          id: `cart-item-price-${item.id}`,
          type: "text",
          text: `$${(item.price * item.quantity).toFixed(2)}`,
          className: "text-sm font-bold text-slate-300 font-mono",
        },
      ],
    }));

    // If the cart is empty, show a fallback message
    const listChildren =
      cart.length === 0
        ? [
            {
              id: "empty-cart-row",
              type: "text",
              text: "Your cart is empty. Go back to the storefront to add items.",
              className: "text-sm text-slate-400 italic py-4",
            },
          ]
        : itemRows;

    const summaryCardChildren = [
      {
        id: "summary-card-title",
        type: "heading",
        level: 2,
        text: "Order Summary",
        className: "text-lg font-headline font-bold text-white mb-2",
      },
      ...listChildren,
      {
        id: "subtotal-row",
        type: "container",
        layout: {
          type: "flex",
          direction: "row",
          align: "center",
          justify: "between",
        },
        className: "mt-4 text-xs text-slate-400",
        children: [
          { id: "subtotal-lbl", type: "text", text: "Subtotal" },
          { id: "subtotal-val", type: "text", text: `$${subtotal.toFixed(2)}`, className: "font-mono" },
        ],
      },
      {
        id: "shipping-row",
        type: "container",
        layout: {
          type: "flex",
          direction: "row",
          align: "center",
          justify: "between",
        },
        className: "text-xs text-slate-400 pb-2 border-b border-white/10",
        children: [
          { id: "shipping-lbl", type: "text", text: "Shipping" },
          { id: "shipping-val", type: "text", text: `$${shipping.toFixed(2)}`, className: "font-mono" },
        ],
      },
      {
        id: "calc-total-row",
        type: "container",
        layout: {
          type: "flex",
          direction: "row",
          align: "center",
          justify: "between",
        },
        className: "mt-4 pt-1",
        children: [
          {
            id: "calc-total-lbl",
            type: "text",
            text: "Total Amount",
            className: "text-base font-bold text-white font-headline",
          },
          {
            id: "calc-total-val",
            type: "text",
            text: `$${total.toFixed(2)}`,
            className: "text-xl font-bold text-indigo-400 font-headline font-mono",
          },
        ],
      },
    ];

    const findAndMutate = (node: any) => {
      if (!node) return;
      if (node.id === "items-summary-card") {
        node.children = summaryCardChildren;
      } else if (node.id === "action-complete-checkout") {
        node.text = cart.length === 0 ? "Cart is Empty" : `🔒 Pay $${total.toFixed(2)}`;
        node.disabled = cart.length === 0;
      }
      
      // Also inject mock values into input fields for professional aesthetics!
      if (node.id === "firstname-input") {
        node.children = [
          {
            id: "firstname-input-val",
            type: "text",
            text: "Alex",
            className: "text-slate-200 text-sm font-sans"
          }
        ];
      } else if (node.id === "lastname-input") {
        node.children = [
          {
            id: "lastname-input-val",
            type: "text",
            text: "Mercer",
            className: "text-slate-200 text-sm font-sans"
          }
        ];
      } else if (node.id === "email-input") {
        node.children = [
          {
            id: "email-input-val",
            type: "text",
            text: "alex.mercer@cogo.xyz",
            className: "text-slate-200 text-sm font-sans"
          }
        ];
      }

      if (node.children) {
        node.children.forEach(findAndMutate);
      }
    };

    findAndMutate(cloned.root);
    return cloned;
  }, [page, cart, subtotal, shipping, total]);

  const handleEvent = (elementId: string, eventType: string) => {
    if (eventType === "tap") {
      if (elementId === "checkout-title-logo") {
        onNavigate("/storefront");
      } else if (elementId === "action-complete-checkout") {
        if (cart.length > 0) {
          setIsCheckoutSuccess(true);
        }
      }
    }
  };

  return (
    <>
      <SchemaV4PageRenderer
        page={pageWithState}
        dispatchEvent={handleEvent}
        navigate={onNavigate}
        colorScheme="dark"
      />
      {isCheckoutSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-6 transition-all duration-300">
          <div className="max-w-md w-full bg-slate-900/90 border border-white/10 backdrop-blur-xl p-8 rounded-3xl shadow-2xl text-center flex flex-col items-center gap-6 scale-100 transition-all duration-300">
            <div className="w-16 h-16 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 text-3xl shadow-inner">
              ✓
            </div>
            <div>
              <h2 className="text-2xl font-headline font-bold text-white mb-2">Order Confirmed!</h2>
              <p className="text-slate-300 text-sm font-sans leading-relaxed">
                Your payment of <span className="text-indigo-400 font-semibold font-mono">${total.toFixed(2)}</span> has been securely processed by the Cogo dynamic runtime.
              </p>
            </div>
            <div className="w-full bg-white/5 border border-white/5 rounded-2xl p-4 text-left">
              <h4 className="text-[10px] font-bold text-slate-400 tracking-widest mb-2 font-headline">DELIVERY DETAILS</h4>
              <p className="text-xs text-slate-200">Alex Mercer</p>
              <p className="text-xs text-slate-400 mt-1">1204 Glassmorphism Ave, San Francisco, CA</p>
            </div>
            <button
              onClick={() => {
                onClearCart();
                setIsCheckoutSuccess(false);
                onNavigate("/storefront");
              }}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3 px-6 rounded-xl shadow-lg shadow-indigo-500/20 transition transform active:scale-95 text-center cursor-pointer font-headline"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
      <ProvenanceFooter />
    </>
  );
}

export function Checkout(props: CheckoutProps) {
  return (
    <Suspense fallback={<div className="p-8 text-stone-500 bg-slate-950 min-h-screen text-center">Loading Checkout…</div>}>
      <CheckoutInner {...props} />
    </Suspense>
  );
}
